import FeaturesMap from '@apps-common/ui-maps/FeaturesMap';
import PropTypes from 'prop-types';
import React from 'react';
import {
  Box,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import {GeoJSON2SVG} from 'geojson2svg';
import {Virtuoso} from 'react-virtuoso';
import {styled} from '@mui/material/styles';
import {useTranslation} from 'react-i18next';

import {getExtent} from '@explorer/util/geom.js';

const TILE_SIZE = 45;
const SVG_MARGIN = 5;
const MAP_SIZE = 425;
const COMMON_FEATURE_NAME_PROPERTIES = [
  'name',
  'lsadname',
  'title',
  'description',
  'id',
  'fid',
  'fips',
  'fipscode',
];

const FeatureThumbnail = styled('svg')(({theme}) => ({
  outline: `2px solid ${theme.palette.grey[200]}`,
  background: 'white',
  marginRight: theme.spacing(1),
  borderRadius: 5,
  stroke: theme.palette.grey[500],
  fill: theme.palette.grey[100],
  strokeWidth: 2,
}));

const FeatureTile = ({feature, selected, onClick, featureKey, featureIdx}) => {
  const {t} = useTranslation();
  const featureExtent = getExtent(feature.geometry, 0.1);
  const converter = new GeoJSON2SVG({
    viewportSize: {
      width: TILE_SIZE - SVG_MARGIN,
      height: TILE_SIZE - SVG_MARGIN,
    },
    mapExtent: featureExtent,
  });

  const getPaths = () => {
    return {__html: converter.convert(feature.geometry)};
  };

  const nameAttributes = Object.keys(feature.properties)
    .filter(key => COMMON_FEATURE_NAME_PROPERTIES.includes(key.toLowerCase()))
    .slice(0, 1);

  const attributeInfo =
    nameAttributes.length === 0 ? (
      <Typography color="textSecondary">
        {t('uploadDialog.attributeInfoPlaceholder', {
          idx: featureIdx + 1,
        })}
      </Typography>
    ) : (
      <React.Fragment>
        {nameAttributes.map(key => (
          <div key={key}>
            <b>{key}</b> {'' + feature.properties[key]}
          </div>
        ))}
      </React.Fragment>
    );
  return (
    <ListItem
      secondaryAction={
        <Checkbox
          edge="end"
          onChange={onClick}
          checked={selected}
          inputProps={{'aria-labelledby': `${featureKey}-text`}}
        />
      }
      disablePadding
    >
      <ListItemButton onClick={onClick} selected={selected}>
        <FeatureThumbnail
          className="feature"
          width={TILE_SIZE}
          height={TILE_SIZE}
          dangerouslySetInnerHTML={getPaths()}
        />
        <ListItemText id={`${featureKey}-text`}>{attributeInfo}</ListItemText>
      </ListItemButton>
    </ListItem>
  );
};

FeatureTile.propTypes = {
  feature: PropTypes.object,
  selected: PropTypes.bool,
  onClick: PropTypes.func,
  featureKey: PropTypes.string,
  featureIdx: PropTypes.number,
};

export const FeaturePicker = ({features, selectedFeatures, onChange}) => {
  const filteredFeatures = Object.keys(selectedFeatures)
    .filter(key => selectedFeatures[key])
    .map(key => features[key]);

  return (
    <Box display="flex" flexGrow={1}>
      <Box
        sx={{
          flexGrow: 0,
          flexShrink: 0,
          minHeight: MAP_SIZE,
          width: MAP_SIZE,
        }}
      >
        <FeaturesMap
          features={features}
          selectedFeatures={filteredFeatures}
          onFeatureClick={feature => onChange(feature.id)}
          padding={[10, 50, 10, 10]}
          featuresStyle="unselected"
          showLayerSwitcher={false}
        />
      </Box>
      <Box marginLeft={1} flexGrow="1">
        <Virtuoso
          data={features}
          components={{
            List,
          }}
          itemContent={(idx, feature) => {
            const key = JSON.stringify(Object.assign(feature, {idx}));
            return (
              <FeatureTile
                key={key}
                featureKey={feature.id}
                featureIdx={idx}
                feature={feature}
                selected={selectedFeatures[idx] === true}
                onClick={() => {
                  onChange(idx);
                }}
              />
            );
          }}
        />
      </Box>
    </Box>
  );
};

FeaturePicker.propTypes = {
  features: PropTypes.array,
  selectedFeatures: PropTypes.object,
  onChange: PropTypes.func,
};
